/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { getRouteInitData } from '@gh/common-shared';
import { ActivatedRoute } from '@angular/router';
import { BaseDesignDetailsFormState } from './base-design-details-form.service';
import { AbstractFormComponent } from '@gh/builder-shared';
import { assign } from 'lodash';
import { TranslationService } from '@gh/core-mls';
import { BaseDesignDetails, BaseDesignImage } from '@gh/builder-shared-data';

@Component({
  standalone: false,
  selector:      'gh-base-design-details',
  styleUrls:     ['base-design-details.component.scss'],
  templateUrl:   'base-design-details.component.html',
  encapsulation: ViewEncapsulation.None,
  providers:     [BaseDesignDetailsFormState],
})
export class BaseDesignDetailsComponent extends AbstractFormComponent<BaseDesignDetails> implements OnInit {
  @ViewChild('gallery') galleryEl: ElementRef;
  get floorPlaceholder(): string {
    return `${ this.translate.get('lbl_floor_area') },  ${ this.baseDesignDetails.floorArea.unit }`;
  }
  activeTab: 'FACADE' | 'IMAGES' | 'FLOOR_PLANS';
  baseDesignDetails: BaseDesignDetails;
  galleryItems: BaseDesignImage[];
  isShowImageModal: boolean;
  originImage: string;
  imageWidth: number;
  isZoomInDisable: boolean;
  isZoomOutDisable: boolean;
  disableNext: boolean;
  disablePrev: boolean;
  defaultImageWidth = 60;
  zoomStep = 10;
  maxZoom = 100;
  minZoom = 30;

  constructor(
    public route: ActivatedRoute,
    public form: BaseDesignDetailsFormState,
    public translate: TranslationService,
  ) {
    super();
    this.activeTab = 'FACADE';
    this.imageWidth = this.defaultImageWidth;
  }

  ngOnInit(): void {
    const { baseDesignDetails } = getRouteInitData(this.route);
    assign(this, { baseDesignDetails });
    this.galleryItems = this.baseDesignDetails.facades;
    this.form.build(baseDesignDetails);
  }

  onSelectTab(event: any): void {
    this.activeTab = event;

    switch (this.activeTab) {
      case 'FACADE':
        this.galleryItems = this.baseDesignDetails.facades;
        break;
      case 'IMAGES':
        this.galleryItems = this.baseDesignDetails.images;
        break;
      case 'FLOOR_PLANS':
        this.galleryItems = this.baseDesignDetails.floorPlans;
        break;
      default:
        this.galleryItems = this.baseDesignDetails.facades;
        break;
    }
    this.galleryEl.nativeElement.scrollTo({top: 0});
  }

  onImageClick(item: BaseDesignImage, i: number): void {
    this.originImage = item.links.original;
    this.isShowImageModal = true;
    this.disablePrev = !i;
    this.disableNext = i === this.galleryItems.length - 1;
  }

  closeImageModal(): void {
    this.isShowImageModal = false;
    this.imageWidth = this.defaultImageWidth;
    this.recalculateZoomStatus();
  }

  onImageContainerClick(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onZoomIn(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.imageWidth < this.maxZoom) {
      this.imageWidth = this.imageWidth + this.zoomStep;
    }
    this.recalculateZoomStatus();
  }

  onZoomOut(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.imageWidth > this.minZoom) {
      this.imageWidth = this.imageWidth - this.zoomStep;
    }
    this.recalculateZoomStatus();
  }

  onNext(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    let index;
    this.galleryItems.forEach((item, i) => {
      if (item.links.original === this.originImage) {
        index = i + 1;
      }
    });
    if (index && this.galleryItems[index]) {
      this.originImage = this.galleryItems[index].links.original;
    }
    this.imageWidth = this.defaultImageWidth;
    if (index === this.galleryItems.length - 1) {
      this.disableNext = true;

    }
    if (this.galleryItems.length > 1) {
      this.disablePrev = false;
    }
  }

  onPrev(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    let index;
    this.galleryItems.forEach((item, i) => {
      if (item.links.original === this.originImage) {
        index = i - 1;
      }
    });
    if (index >= 0  && this.galleryItems[index]) {
      this.originImage = this.galleryItems[index].links.original;
    }
    this.imageWidth = this.defaultImageWidth;
    if (!index) {
      this.disablePrev = true;
    }
    if (this.galleryItems.length > 1) {
      this.disableNext = false;
    }
  }

  private recalculateZoomStatus(): void {
    this.isZoomInDisable = this.imageWidth === this.maxZoom;
    this.isZoomOutDisable = this.imageWidth === this.minZoom;
  }
}
